import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const offers = [
  {
    icon: "ri-movie-2-line",
    title: "海量字幕资源",
    desc: "覆盖电影、电视剧、综艺、动漫等类型的免费高清中文字幕，持续更新。",
  },
  {
    icon: "ri-search-2-line",
    title: "高效检索筛选",
    desc: "支持按语言、格式、更新时间筛选与多维度排序，快速定位所需字幕。",
  },
  {
    icon: "ri-eye-line",
    title: "在线字幕预览",
    desc: "下载前可在线浏览字幕文本片段，提前确认翻译风格与版本是否匹配。",
  },
  {
    icon: "ri-download-2-line",
    title: "多格式下载",
    desc: "提供 SRT、ASS 等常用字幕格式，适配主流播放器与片源版本。",
  },
];

const steps = [
  { title: "选择分类或搜索", desc: "从电影、电视剧、综艺、动漫分类进入，或直接搜索影片名称。" },
  { title: "筛选匹配字幕", desc: "根据字幕语言、格式、更新时间等条件筛选出适合的版本。" },
  { title: "在线预览确认", desc: "切换不同字幕版本，预览文本片段确认翻译与时间轴风格。" },
  { title: "下载并外挂播放", desc: "下载字幕文件，在播放器中加载，即可无障碍观看影视作品。" },
];

const contacts = [
  { icon: "ri-mail-line", label: "反馈邮箱", value: "feedback@zimu-online.demo" },
  { icon: "ri-question-answer-line", label: "用户反馈", value: "通过下方表单提交" },
  { icon: "ri-time-line", label: "更新频率", value: "资源每日更新同步" },
];

export default function About() {
  useDocumentTitle(
    "关于我们 - 中文在线字幕免费高清",
    "了解中文在线字幕免费高清站的定位与服务，我们专注提供高清中文字幕资源，并提供用户反馈与联系方式。",
  );

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <section className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            <i className="ri-information-line text-sm"></i>
            关于本站
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            中文在线字幕免费高清
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            中文在线字幕免费高清是一个专注于免费高清中文字幕资源的分享平台。我们面向影视爱好者、字幕需求者以及字幕制作与学习的相关人士，
            提供电影、电视剧、综艺、动漫等作品的字幕检索、在线预览与文件下载服务，帮助大家跨越语言障碍，轻松看懂好内容。
          </p>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            我们希望把散落的字幕资源整理得更有条理：每一条字幕都标注了类型、年份、格式、语言、文件大小与更新时间，
            让你在下载之前就能判断是否匹配自己的片源版本。站内所有内容仅供学习交流，请支持正版影视作品。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/category/movie"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-folder-2-line text-lg"></i>
              浏览字幕分类
            </Link>
            <Link
              to="/search"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300"
            >
              <i className="ri-search-line text-lg"></i>
              搜索字幕资源
            </Link>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">我们提供什么</h2>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {offers.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 rounded-lg bg-background-100 p-6 md:p-8">
        <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">如何使用本站</h2>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <div key={step.title} className="relative rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="font-heading text-2xl font-bold text-primary-300">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-2 font-heading text-sm font-semibold text-foreground-950">{step.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-500">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
        <div className="space-y-4">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系方式</h2>
          <p className="text-sm leading-relaxed text-foreground-500">
            遇到问题、想申请字幕或有意合作，都可以通过以下方式联系我们。
          </p>
          {contacts.map((contact) => (
            <div
              key={contact.label}
              className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className={`${contact.icon} text-xl`}></i>
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">{contact.label}</p>
                <p className="truncate text-sm font-medium text-foreground-900">{contact.value}</p>
              </div>
            </div>
          ))}
          <div className="rounded-md border border-dashed border-background-300 bg-background-50 p-3 text-xs leading-relaxed text-foreground-400">
            本站为字幕资源展示演示站点，不含真实播放、支付或上传功能。
          </div>
        </div>

        <div>
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            给我们留言
          </h2>
          <FeedbackForm />
        </div>
      </section>

      <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6 text-center md:p-8">
        <h2 className="font-heading text-lg font-bold text-foreground-950">版权声明</h2>
        <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          本站提供的字幕资源均来自网络整理，仅供个人学习、研究与交流使用，请勿用于任何商业用途。
          影视作品的著作权归其合法权利人所有，请支持正版影视作品。如本站内容无意侵犯了您的权益，
          请通过上方邮箱联系我们，我们将及时处理。
        </p>
        <p className="mt-4 text-xs text-foreground-400">
          © 2026 中文在线字幕免费高清 · All rights reserved.
        </p>
      </section>
    </div>
  );
}